<script setup lang="ts">
import { onMounted, watch, ref } from 'vue'
import { apiClient, AccessMode } from '@sidd/chatbot-core'
import type { IAnalyticsCallback } from '@sidd/chatbot-core'
import { Spinner } from '@/components/Spinner'
import { useWebDirectChatbot } from './composables/useWebDirectChatbot'
import { useKeycloak } from './composables/useKeycloak'
import { useDataLakeAnalytics } from './composables/useDataLakeAnalytics'
import WebDirectLayout from './components/WebDirectLayout/WebDirectLayout.vue'
import WebDirectError from './components/WebDirectError/WebDirectError.vue'
import { WebDirectLogin } from './components/WebDirectLogin'

const { chatbot, isLoading, error, requiresAuth, loadChatbot, retry } =
  useWebDirectChatbot()

const {
  state: authState,
  initialize: initializeKeycloak,
  login,
  logout,
  getAuthCallback,
  reset: resetKeycloak,
} = useKeycloak()

const isAuthInitializing = ref(false)
const hasAttemptedLogin = ref(false)
const lastTenantId = ref<string | null>(null)
const lastAccessMode = ref<AccessMode | null>(null)
const analyticsCallback = ref<IAnalyticsCallback | undefined>(undefined)

onMounted(async () => {
  await loadChatbot()
})

watch(chatbot, async config => {
  if (!config) return

  document.title = config.name ? `${config.name} | Ask Sidd` : 'Ask Sidd'

  if (!config.isActive) return

  const metaDescription = document.querySelector('meta[name="description"]')
  if (metaDescription && config.description) {
    metaDescription.setAttribute('content', config.description)
  }

  const ogTitle = document.querySelector('meta[property="og:title"]')
  if (ogTitle && config.name) {
    ogTitle.setAttribute('content', `${config.name} | Ask Sidd`)
  }

  const ogDescription = document.querySelector(
    'meta[property="og:description"]'
  )
  if (ogDescription && config.description) {
    ogDescription.setAttribute('content', config.description)
  }

  apiClient.clearAuth()
  apiClient.setAuthCallback(null)

  const isTenantChanged =
    lastTenantId.value !== null && lastTenantId.value !== config.tenantId
  const isAccessModeChanged =
    lastAccessMode.value !== null && lastAccessMode.value !== config.accessMode

  if (config.accessMode === AccessMode._Public) {
    if (isTenantChanged || isAccessModeChanged) {
      resetKeycloak(null)
    }
  } else {
    isAuthInitializing.value = true
    try {
      if (isTenantChanged || isAccessModeChanged) {
        resetKeycloak(config.tenantId)
      }
      await initializeKeycloak(config.tenantId)
      apiClient.setAuthCallback(getAuthCallback())

      const { createAnalyticsCallback } =
        useDataLakeAnalytics(getAuthCallback())
      analyticsCallback.value = createAnalyticsCallback()
    } finally {
      isAuthInitializing.value = false
    }
  }

  lastTenantId.value = config.tenantId
  lastAccessMode.value = config.accessMode
})

watch(
  [
    requiresAuth,
    () => authState.value.isInitialized,
    () => authState.value.isAuthenticated,
  ],
  async ([needsAuth, isInitialized, isAuthenticated]) => {
    if (
      !needsAuth ||
      !isInitialized ||
      isAuthenticated ||
      isAuthInitializing.value ||
      hasAttemptedLogin.value
    ) {
      return
    }

    hasAttemptedLogin.value = true
    await handleLogin()
  }
)

async function handleLogin(): Promise<void> {
  if (!chatbot.value) return

  isAuthInitializing.value = true
  try {
    await initializeKeycloak(chatbot.value.tenantId)
    await login()
  } finally {
    isAuthInitializing.value = false
  }
}

function handleRetry(): void {
  retry()
}

async function webDirectGetAuthInfo() {
  const callback = getAuthCallback()
  const auth = await callback()
  return {
    accessToken: auth.token,
    userTenant: auth.tenantId,
    userId: auth.userId,
  }
}
</script>

<template>
  <div
    v-if="isLoading || isAuthInitializing"
    class="flex flex-col min-h-dvh items-center justify-center bg-background"
    role="status"
    aria-label="Loading chatbot"
  >
    <Spinner size="xl" />
    <span class="text-sm text-muted-foreground mt-4">Loading...</span>
  </div>

  <WebDirectError
    v-else-if="error"
    :type="error"
    :logo-url="chatbot?.logoUrl"
    @retry="handleRetry"
  />

  <WebDirectLogin
    v-else-if="
      requiresAuth && authState.isInitialized && !authState.isAuthenticated
    "
    :logo-url="chatbot?.logoUrl"
    :name="chatbot?.name"
    @login="handleLogin"
  />

  <WebDirectLayout
    v-else-if="chatbot"
    :chatbot-id="chatbot.chatbotId"
    :tenant-id="chatbot.tenantId"
    :user-id="authState.userId"
    :get-auth-info="requiresAuth ? webDirectGetAuthInfo : undefined"
    :logo-url="chatbot.logoUrl"
    :name="chatbot.name"
    :analytics-callback="analyticsCallback"
    :is-authenticated="authState.isAuthenticated"
    :user-name="authState.userName"
    :user-display-name="authState.userDisplayName"
    @logout="logout"
  />
</template>
